// GENERATED by helm-console/scripts/ensure-style-runtime.mjs — DO NOT EDIT.
// Verbatim copy of helm-bridge/src/client/glass-hover.ts. Regenerated every console
// build; edits here are overwritten. Owner correction 2026-09-07: the style
// substrate (backgrounds/canvas, reduce-effects) runs on the Bridge home too.
// glass-hover.ts — GLASS's SPECULAR HIGHLIGHT tracker.
//
// Owner checkpoint 2 redlined the card hover; this module is the JS half of
// that ruling and every clause below is one of its numbered requirements.
//
//   R-H1  The hover is ONE small reflection, not a wash. The alpha ceiling
//         and the bounded radius live in styles.css (--glass-spec-core /
//         --glass-spec-mid / --glass-spec-radius) and are parsed back out of
//         the stylesheet by scripts/style-contrast.mjs. This file never
//         computes a colour — it only says WHERE.
//   R-H2  CONTINUOUS tracking, never quantized presets. ONE delegated,
//         passive `pointermove` listener on the app root resolves the hovered
//         card with closest('.card'); the handler stores card-relative x/y
//         and schedules AT MOST ONE requestAnimationFrame per frame; the
//         frame callback writes --mx / --my / --spec as inline custom
//         properties. Per frame the page pays a gradient position and an
//         opacity — no layout write, no style read beyond the hovered card's
//         own getBoundingClientRect(), no class swap, no canvas.
//   R-H2  Reset on pointerout whose relatedTarget is outside the tracked
//         card, on pointercancel, and on any style/theme/reduce flip. Moving
//         between children of the SAME card must not reset (no flicker), and
//         a card-to-card handoff clears the previous card first.
//   R-H3  INERT under every reduce lane — the CSS removes the pseudo-element
//         outright, and this handler becomes a no-op in exactly the same
//         conditions, because writing props that CSS then ignores is how a
//         "disabled" effect leaves state behind. Under any reduce lane,
//         hovering a card must leave ZERO inline highlight props.
//
// The tracker is armed only while GLASS is the painted style. Under the
// identity style install() has run but the listener returns on its first
// line, so Blueprint pays one `closest()` call per pointermove at most.
// GLASS itself IS reachable in the shipped app — the style picker launched
// in #493 (STYLE_PICKER_SEAT_DEFAULT = true) — so the armed path below is
// live code for anyone who picks it, not a dark-launched branch.

import { effectsReduced, onEffectsChange } from './effects-seat.js';

/** The custom properties this module owns. Exported because the gate and
 * the smoke assert their ABSENCE after a reset, and a literal typed twice is
 * a literal that can disagree with itself. */
export const SPEC_VARS = ['--mx', '--my', '--spec'] as const;

/** The tier the highlight rides. One selector, named once. */
export const SPEC_TARGET = '.card';

let installed = false;
let armed = false;
let host: HTMLElement | null = null;
let card: HTMLElement | null = null;
let px = 0;
let py = 0;
let raf = 0;

/** True when the tracker must do nothing at all: not the painted style, or
 * any reduce lane is on. */
export function specularInert(): boolean {
  return !armed || effectsReduced();
}

/** Drop every inline prop from the tracked card and forget it. Safe to call
 * when nothing is tracked. */
export function clearSpecular(): void {
  if (raf) {
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf);
    raf = 0;
  }
  if (!card) return;
  for (const name of SPEC_VARS) card.style.removeProperty(name);
  card = null;
}

function write(): void {
  raf = 0;
  if (!card || specularInert()) return;
  card.style.setProperty('--mx', `${px.toFixed(1)}px`);
  card.style.setProperty('--my', `${py.toFixed(1)}px`);
  card.style.setProperty('--spec', '1');
}

function onPointerMove(e: PointerEvent): void {
  const t = e.target as Element | null;
  const next = t && typeof t.closest === 'function' ? (t.closest(SPEC_TARGET) as HTMLElement | null) : null;
  if (!next || specularInert()) {
    clearSpecular();
    return;
  }
  // Card-to-card handoff: the OUTGOING card is cleared before the incoming
  // one is adopted, or a fast diagonal drag leaves a frozen bloom behind.
  if (next !== card) {
    clearSpecular();
    card = next;
  }
  const r = next.getBoundingClientRect();
  px = e.clientX - r.left;
  py = e.clientY - r.top;
  if (!raf && typeof requestAnimationFrame === 'function') raf = requestAnimationFrame(write);
}

function onPointerOut(e: PointerEvent): void {
  if (!card) return;
  const to = e.relatedTarget as Node | null;
  // Still inside the same card (moved onto a chip, the footer, a button) —
  // NOT a leave. Resetting here is the flicker bug.
  if (to && card.contains(to)) return;
  clearSpecular();
}

/**
 * Install the three listeners once, on the app root. Delegated and passive:
 * a per-card listener set would be O(cards) to attach and would have to be
 * re-attached on every list re-render.
 */
export function installSpecularTracker(root: HTMLElement | null): void {
  if (installed || !root) return;
  host = root;
  host.addEventListener('pointermove', onPointerMove as EventListener, { passive: true });
  host.addEventListener('pointerout', onPointerOut as EventListener);
  host.addEventListener('pointercancel', clearSpecular);
  // A reduce lane flipping mid-hover must strip the props immediately — the
  // CSS stops rendering them, and a stale inline `--spec: 1` would come back
  // the moment the lane flipped off without the pointer having moved.
  onEffectsChange(() => {
    if (specularInert()) clearSpecular();
  });
  installed = true;
}

/**
 * Arm or disarm for the painted style. Called from the ONE apply path
 * (appearance.ts → onStylePainted), so "the style changed" and "the tracker
 * changed" cannot disagree. Disarming clears first: a style switch while
 * hovering must not leave the outgoing style's props on the element.
 */
export function setSpecularArmed(on: boolean): void {
  if (armed === on) return;
  armed = on;
  if (!on) clearSpecular();
}

/** The tracked card, for the smoke/gate arms. */
export function specularCard(): HTMLElement | null {
  return card;
}

/** Test seam. */
export function resetSpecularTracker(): void {
  clearSpecular();
  if (host) {
    host.removeEventListener('pointermove', onPointerMove as EventListener);
    host.removeEventListener('pointerout', onPointerOut as EventListener);
    host.removeEventListener('pointercancel', clearSpecular);
  }
  host = null;
  installed = false;
  armed = false;
}
